import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  ArrowLeftRight,
  Headphones,
  Loader2,
  MapPin,
  Megaphone,
  Percent,
  Plus,
  RefreshCw,
  Sparkles,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { cn } from '@/lib/utils/cn';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import {
  useSetDialerSuccessRate,
  useSpecialDialers,
} from '@/features/dialers/api';
import {
  useAddCampaignToDialer,
  useCampaignAllocation,
  useRemoveCampaignFromDialer,
  useRunningCampaigns,
} from '../api';

interface CellTarget {
  location: string;
  dialerId: string;
  /** Currently-allocated campaigns on this cell. */
  allocated: Array<{ cid: number; cmpName: string }>;
}

type Mode = 'regular' | 'special';

export function CampaignAllocationPage() {
  const allocation = useCampaignAllocation();
  const running = useRunningCampaigns();
  const add = useAddCampaignToDialer();
  const remove = useRemoveCampaignFromDialer();
  const specialDialers = useSpecialDialers();
  const setSuccess = useSetDialerSuccessRate();

  const [mode, setMode] = useState<Mode>('regular');
  const [editing, setEditing] = useState<CellTarget | null>(null);
  const [specialTarget, setSpecialTarget] = useState<CellTarget | null>(null);

  // ── Campaign search filter ──
  type FilterChoice = 'none' | 'name' | 'id';
  const [filterChoice, setFilterChoice] = useState<FilterChoice>('none');
  const [filterDraft, setFilterDraft] = useState('');
  const [filterData, setFilterData] = useState('');

  const applyFilter = () => {
    setFilterData(filterDraft);
    setSelected(new Map());
  };
  const clearFilter = () => {
    setFilterChoice('none');
    setFilterDraft('');
    setFilterData('');
    setSelected(new Map());
  };

  const isFilterActive = Boolean(filterData) && filterChoice !== 'none';
  const matchesFilter = (cid: number, cmpName: string) =>
    isFilterActive &&
    (filterChoice === 'id'
      ? String(cid) === filterData
      : cmpName.toLowerCase().includes(filterData.toLowerCase()));

  interface MatchItem {
    key: string;
    dialerId: string;
    location: string;
    cid: number;
    cmpName: string;
  }
  type DialerGroup = { location: string; dialerId: string; ids: string[]; names: string[] };
  const [selected, setSelected] = useState<Map<string, MatchItem>>(new Map());
  const [bulkRemoving, setBulkRemoving] = useState(false);

  const toggleSelect = (item: MatchItem) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.key)) next.delete(item.key);
      else next.set(item.key, item);
      return next;
    });
  };

  /** Set of dialerIds (stringified) flagged as "special". Empty in regular mode. */
  const specialIds = useMemo(
    () =>
      new Set((specialDialers.data ?? []).map((d) => String(d.dialerId))),
    [specialDialers.data],
  );

  /** Flatten the nested allocation map into list-style cells. */
  const cells = useMemo(() => {
    if (!allocation.data) return [];
    const out: Array<{
      location: string;
      dialerId: string;
      allocated: Array<{ cid: number; cmpName: string }>;
    }> = [];
    for (const [location, dialers] of Object.entries(allocation.data)) {
      for (const [dialerId, campaigns] of Object.entries(dialers)) {
        out.push({
          location,
          dialerId,
          allocated: Object.values(campaigns).map((c) => ({
            cid: Number(c.cid),
            cmpName: c.cmpName,
          })),
        });
      }
    }
    return out.sort((a, b) =>
      a.location === b.location
        ? a.dialerId.localeCompare(b.dialerId)
        : a.location.localeCompare(b.location),
    );
  }, [allocation.data]);

  /**
   * Filter cells to whichever pool the user picked. Regular mode HIDES
   * special dialers; special mode SHOWS only them. Mirrors the legacy
   * ManageCampaignSuperadmin two-mode toggle.
   */
  const filteredCells = useMemo(() => {
    if (mode === 'regular') {
      return cells.filter((c) => !specialIds.has(c.dialerId));
    }
    return cells.filter((c) => specialIds.has(c.dialerId));
  }, [cells, mode, specialIds]);

  /** Group cells by location for the rendered card grid. */
  const grouped = useMemo(() => {
    let source = filteredCells;
    if (filterData && filterChoice !== 'none') {
      const q = filterData.toLowerCase();
      source = filteredCells.filter((c) =>
        c.allocated.some((a) =>
          filterChoice === 'id'
            ? String(a.cid) === filterData
            : a.cmpName.toLowerCase().includes(q),
        ),
      );
    }

    const map = new Map<string, typeof filteredCells>();
    for (const c of source) {
      const list = map.get(c.location) ?? [];
      list.push(c);
      map.set(c.location, list);
    }
    return Array.from(map.entries());
  }, [filteredCells, filterData, filterChoice]);

  /** Flat list of every chip that currently matches the active filter — powers "Select all". */
  const allMatches = useMemo(() => {
    const out: MatchItem[] = [];
    for (const [, locCells] of grouped) {
      for (const cell of locCells) {
        for (const c of cell.allocated) {
          if (matchesFilter(c.cid, c.cmpName)) {
            out.push({
              key: `${cell.dialerId}:${c.cid}`,
              dialerId: cell.dialerId,
              location: cell.location,
              cid: c.cid,
              cmpName: c.cmpName,
            });
          }
        }
      }
    }
    return out;
  }, [grouped, filterData, filterChoice]);

  const selectAllMatches = () => setSelected(new Map(allMatches.map((m) => [m.key, m])));
  const clearSelection = () => setSelected(new Map());

  const handleBulkRemove = async () => {
    const groups = new Map<string, DialerGroup>();
    for (const item of selected.values()) {
      const g = groups.get(item.dialerId) ?? {
        location: item.location,
        dialerId: item.dialerId,
        ids: [],
        names: [],
      };
      g.ids.push(String(item.cid));
      g.names.push(item.cmpName);
      groups.set(item.dialerId, g);
    }
    setBulkRemoving(true);
    const results = await Promise.allSettled(
      Array.from(groups.values()).map((g) =>
        remove.mutateAsync({
          campaignIds: g.ids.join(','),
          campaignNames: g.names.join(','),
          location: g.location,
          dialerId: g.dialerId,
        }),
      ),
    );
    setBulkRemoving(false);
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed === 0) {
      toast.success(`Removed ${selected.size} campaign${selected.size === 1 ? '' : 's'}.`);
    } else {
      toast.error(`Removed from ${groups.size - failed} of ${groups.size} dialers — some failed.`);
    }
    allocation.refetch();
    setSelected(new Map());
  };

  const canView = usePermission('Manage Campaign.Manage Campaigns', BASE_PERMISSION.VIEW);
  const canEdit = usePermission('Manage Campaign.Manage Campaigns', BASE_PERMISSION.EDIT);

  if (!canView) return <Navigate to="/" replace />;

  if (allocation.isLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading allocations…
      </div>
    );
  }

  return (
    <ScrollPage>
      <PageHeader
        title="Campaign Allocation"
        description={
          mode === 'regular'
            ? 'Re-allocate running campaigns across dialers, grouped by location.'
            : 'Pin campaigns to special dialers with a success-rate percentage cap.'
        }
        actions={
          <Button
            variant="outline"
            size="icon"
            onClick={() => allocation.refetch()}
            disabled={allocation.isFetching}
            aria-label="Refresh"
          >
            <RefreshCw
              className={cn('h-4 w-4', allocation.isFetching && 'animate-spin')}
            />
          </Button>
        }
      />

      <Tabs
        value={mode}
        onValueChange={(v) => {
          setMode(v as Mode);
          setSelected(new Map());
        }}
        className="mb-2"
      >
        <TabsList>
          <TabsTrigger value="regular">
            <Headphones className="mr-1 h-3.5 w-3.5" />
            Regular
          </TabsTrigger>
          <TabsTrigger value="special">
            <Sparkles className="mr-1 h-3.5 w-3.5" />
            Special dialers
            {specialDialers.data && specialDialers.data.length > 0 && (
              <span className="ml-1.5 rounded-sm bg-muted-foreground/15 px-1 text-2xs">
                {specialDialers.data.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* ── Campaign search filter ── */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2">
        <span className="text-xs font-medium text-muted-foreground">Filter:</span>
        <Select
          value={filterChoice}
          onValueChange={(v) => {
            setFilterChoice(v as FilterChoice);
            setFilterDraft('');
            setFilterData('');
          }}
        >
          <SelectTrigger className="h-8 w-44">
            <SelectValue placeholder="No filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No filter</SelectItem>
            <SelectItem value="name">Campaign Name</SelectItem>
            <SelectItem value="id">Campaign ID</SelectItem>
          </SelectContent>
        </Select>
        {filterChoice !== 'none' && (
          <>
            <Input
              value={filterDraft}
              inputMode={filterChoice === 'id' ? 'numeric' : 'text'}
              onChange={(e) => {
                const v = e.target.value;
                setFilterDraft(filterChoice === 'id' ? v.replace(/[^\d]/g, '') : v);
              }}
              placeholder={filterChoice === 'id' ? 'Enter campaign ID' : 'Enter campaign name'}
              className="h-8 w-48"
            />
            <Button size="sm" onClick={applyFilter} disabled={!filterDraft}>
              Apply
            </Button>
            <Button variant="ghost" size="sm" onClick={clearFilter}>
              Clear
            </Button>
          </>
        )}
      </div>

      {isFilterActive && allMatches.length > 0 && canEdit && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2">
          <span className="text-xs text-muted-foreground">
            {allMatches.length} match{allMatches.length === 1 ? '' : 'es'} found
            {selected.size > 0 && ` · ${selected.size} selected`}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={selectAllMatches}
            disabled={selected.size === allMatches.length}
          >
            Select all matches
          </Button>
          {selected.size > 0 && (
            <>
              <Button size="sm" variant="ghost" onClick={clearSelection}>
                Clear selection
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleBulkRemove}
                disabled={bulkRemoving}
                className="ml-auto"
              >
                {bulkRemoving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <X className="h-4 w-4" />
                )}
                Remove {selected.size} from dialer{selected.size === 1 ? '' : 's'}
              </Button>
            </>
          )}
        </div>
      )}

      {grouped.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {filterData
              ? 'No dialers found with this campaign assigned.'
              : 'No dialers configured yet.'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {grouped.map(([location, locCells]) => (
            <Card key={location}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  {location}
                </CardTitle>
                <CardDescription>
                  {locCells.length} dialer{locCells.length === 1 ? '' : 's'} in this
                  location
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {locCells.map((cell) => (
                  <div
                    key={`${cell.location}-${cell.dialerId}`}
                    className="flex flex-wrap items-start gap-3 rounded-md border border-border bg-card p-3"
                  >
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Headphones className="h-4 w-4 text-muted-foreground" />
                      Dialer #{cell.dialerId}
                    </div>
                    <div className="flex flex-1 flex-wrap items-center gap-1.5">
                      {cell.allocated.length === 0 ? (
                        <span className="text-xs text-muted-foreground">
                          No campaigns assigned
                        </span>
                      ) : (
                        cell.allocated.map((c) => {
                          const isMatch = matchesFilter(c.cid, c.cmpName) && canEdit;
                          if (!isMatch) {
                            return (
                              <Badge key={c.cid} variant="outline" className="font-normal">
                                <Megaphone className="h-3 w-3" />
                                {c.cmpName}
                              </Badge>
                            );
                          }
                          const key = `${cell.dialerId}:${c.cid}`;
                          const checked = selected.has(key);
                          return (
                            <button
                              key={c.cid}
                              type="button"
                              onClick={() =>
                                toggleSelect({
                                  key,
                                  dialerId: cell.dialerId,
                                  location: cell.location,
                                  cid: c.cid,
                                  cmpName: c.cmpName,
                                })
                              }
                              className={cn(
                                'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium transition-colors',
                                checked
                                  ? 'border-destructive bg-destructive/10 text-destructive'
                                  : 'border-primary bg-primary/5 text-primary ring-2 ring-primary ring-offset-1',
                              )}
                            >
                              <Checkbox checked={checked} className="pointer-events-none h-3.5 w-3.5" />
                              <Megaphone className="h-3 w-3" />
                              {c.cmpName}
                              <span className="font-mono text-2xs text-muted-foreground">
                                #{c.cid}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          mode === 'special'
                            ? setSpecialTarget(cell)
                            : setEditing(cell)
                        }
                      >
                        {mode === 'special' ? (
                          <Percent className="h-3.5 w-3.5" />
                        ) : (
                          <ArrowLeftRight className="h-3.5 w-3.5" />
                        )}
                        {mode === 'special' ? 'Pin campaign' : 'Edit'}
                      </Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <SpecialPinDialog
        target={specialTarget}
        runningCampaigns={running.data ?? []}
        loadingRunning={running.isFetching}
        onClose={() => setSpecialTarget(null)}
        onSubmit={({ cid, cmpName, percentage }) => {
          if (!specialTarget) return;
          setSuccess.mutate(
            {
              dialerId: Number(specialTarget.dialerId),
              campaignId: cid,
              campaignName: cmpName,
              location: specialTarget.location,
              percentage,
            },
            {
              onSuccess: (res) => {
                toast.success(res?.message ?? 'Campaign pinned');
                allocation.refetch();
                setSpecialTarget(null);
              },
              onError: () => toast.error('Could not pin the campaign.'),
            },
          );
        }}
        submitting={setSuccess.isPending}
      />

      <EditAllocationDialog
        target={editing}
        runningCampaigns={running.data ?? []}
        loadingRunning={running.isFetching}
        filterChoice={filterChoice}
        filterData={filterData}
        onClose={() => setEditing(null)}
        onAdd={(picks) => {
          if (!editing) return;
          add.mutate(
            {
              campaignIds: picks.map((p) => String(p.cid)).join(','),
              campaignNames: picks.map((p) => p.cmpName).join(','),
              location: editing.location,
              dialerId: editing.dialerId,
            },
            {
              onSuccess: (res) => {
                toast.success(res?.message ?? 'Campaigns added');
                allocation.refetch();
              },
              onError: () => toast.error('Could not add campaigns.'),
            },
          );
        }}
        onRemove={(picks) => {
          if (!editing) return;
          remove.mutate(
            {
              campaignIds: picks.map((p) => String(p.cid)).join(','),
              campaignNames: picks.map((p) => p.cmpName).join(','),
              location: editing.location,
              dialerId: editing.dialerId,
            },
            {
              onSuccess: (res) => {
                toast.success(res?.message ?? 'Campaigns removed');
                allocation.refetch();
              },
              onError: () => toast.error('Could not remove campaigns.'),
            },
          );
        }}
        addPending={add.isPending}
        removePending={remove.isPending}
      />
    </ScrollPage>
  );
}

interface DialogProps {
  target: CellTarget | null;
  runningCampaigns: Array<{ campaignId: number; campaignName: string }>;
  loadingRunning: boolean;
  filterChoice?: string;
  filterData?: string;
  onClose: () => void;
  onAdd: (picks: Array<{ cid: number; cmpName: string }>) => void;
  onRemove: (picks: Array<{ cid: number; cmpName: string }>) => void;
  addPending: boolean;
  removePending: boolean;
}

function EditAllocationDialog({
  target,
  runningCampaigns,
  loadingRunning,
  filterChoice: fc = 'none',
  filterData: fd = '',
  onClose,
  onAdd,
  onRemove,
  addPending,
  removePending,
}: DialogProps) {
  const [selectedToAdd, setSelectedToAdd] = useState<Set<number>>(new Set());
  const [selectedToRemove, setSelectedToRemove] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState('');

  /** Reset selection state when target changes. */
  useEffect(() => {
    setSelectedToAdd(new Set());
    setSelectedToRemove(new Set());
    setFilter('');
  }, [target?.dialerId, target?.location]);

  if (!target) return null;

  const allocatedIds = new Set(target.allocated.map((c) => c.cid));
  const available = runningCampaigns
    .filter((r) => !allocatedIds.has(r.campaignId))
    .filter(
      (r) =>
        !filter ||
        r.campaignName.toLowerCase().includes(filter.toLowerCase()) ||
        String(r.campaignId).includes(filter),
    );

  const toggle = (set: Set<number>, id: number) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const handleAdd = () => {
    const picks = runningCampaigns
      .filter((r) => selectedToAdd.has(r.campaignId))
      .map((r) => ({ cid: r.campaignId, cmpName: r.campaignName }));
    if (picks.length === 0) {
      toast.error('Pick at least one campaign to add.');
      return;
    }
    onAdd(picks);
    setSelectedToAdd(new Set());
  };

  const handleRemove = () => {
    const picks = target.allocated.filter((c) => selectedToRemove.has(c.cid));
    if (picks.length === 0) {
      toast.error('Pick at least one campaign to remove.');
      return;
    }
    onRemove(picks);
    setSelectedToRemove(new Set());
  };

  return (
    <Dialog open={target !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Edit dialer #{target.dialerId} · {target.location}
          </DialogTitle>
          <DialogDescription>
            Select campaigns on either side, then add or remove them.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Available campaigns */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Available campaigns</Label>
              <span className="text-xs text-muted-foreground">
                {available.length} running, not on this dialer
              </span>
            </div>
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search by name or id…"
            />
            <div className="max-h-[40vh] space-y-1 overflow-y-auto rounded-md border border-border p-2">
              {loadingRunning ? (
                <div className="py-6 text-center text-xs text-muted-foreground">
                  Loading running campaigns…
                </div>
              ) : available.length === 0 ? (
                <div className="py-6 text-center text-xs text-muted-foreground">
                  No additional running campaigns to bind.
                </div>
              ) : (
                available.map((c) => {
                  const active = selectedToAdd.has(c.campaignId);
                  return (
                    <button
                      key={c.campaignId}
                      type="button"
                      onClick={() =>
                        setSelectedToAdd((s) => toggle(s, c.campaignId))
                      }
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md border p-2 text-left text-sm transition-colors',
                        active
                          ? 'border-primary bg-primary/5'
                          : 'border-transparent hover:bg-muted/50',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                          active
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-muted-foreground/40',
                        )}
                      >
                        {active && <Plus className="h-2.5 w-2.5" />}
                      </span>
                      <span className="truncate">{c.campaignName}</span>
                      <span className="ml-auto font-mono text-xs text-muted-foreground">
                        #{c.campaignId}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
            <Button
              size="sm"
              onClick={handleAdd}
              disabled={selectedToAdd.size === 0 || addPending}
              className="w-full"
            >
              {addPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Add {selectedToAdd.size} to dialer
            </Button>
          </div>

          {/* Allocated campaigns */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Currently allocated</Label>
              <span className="text-xs text-muted-foreground">
                {target.allocated.length} on this dialer
              </span>
            </div>
            <div className="max-h-[40vh] space-y-1 overflow-y-auto rounded-md border border-border p-2">
              {target.allocated.length === 0 ? (
                <div className="py-6 text-center text-xs text-muted-foreground">
                  Nothing allocated yet.
                </div>
              ) : (
                target.allocated.map((c) => {
                  const active = selectedToRemove.has(c.cid);
                  const isMatch =
                    fd &&
                    fc !== 'none' &&
                    (fc === 'id'
                      ? String(c.cid) === fd
                      : c.cmpName.toLowerCase().includes(fd.toLowerCase()));
                  return (
                    <button
                      key={c.cid}
                      type="button"
                      onClick={() =>
                        setSelectedToRemove((s) => toggle(s, c.cid))
                      }
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md border p-2 text-left text-sm transition-colors',
                        active
                          ? 'border-destructive bg-destructive/5'
                          : isMatch
                            ? 'border-primary bg-primary/5 ring-1 ring-primary'
                            : 'border-transparent hover:bg-muted/50',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                          active
                            ? 'border-destructive bg-destructive text-destructive-foreground'
                            : 'border-muted-foreground/40',
                        )}
                      >
                        {active && <X className="h-2.5 w-2.5" />}
                      </span>
                      <span className="truncate">{c.cmpName}</span>
                      <span className="ml-auto font-mono text-xs text-muted-foreground">
                        #{c.cid}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleRemove}
              disabled={selectedToRemove.size === 0 || removePending}
              className="w-full"
            >
              {removePending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
              Remove {selectedToRemove.size} from dialer
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
 *  Special-dialer pin dialog
 * ============================================================ */

interface SpecialPinDialogProps {
  target: CellTarget | null;
  runningCampaigns: Array<{ campaignId: number; campaignName: string }>;
  loadingRunning: boolean;
  onClose: () => void;
  onSubmit: (args: {
    cid: number;
    cmpName: string;
    percentage: number;
  }) => void;
  submitting: boolean;
}

/**
 * Picks ONE running campaign and a percentage (1–100), then calls
 * /api/obd/dialers/setsuccess via the parent. Mirrors the legacy special-
 * dialer "Add Campaign" modal but as a focused single-campaign flow —
 * pinning is per-campaign so batching wouldn't share semantics.
 */
function SpecialPinDialog({
  target,
  runningCampaigns,
  loadingRunning,
  onClose,
  onSubmit,
  submitting,
}: SpecialPinDialogProps) {
  const [selectedCid, setSelectedCid] = useState<number | null>(null);
  const [percentage, setPercentage] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    setSelectedCid(null);
    setPercentage('');
    setFilter('');
  }, [target?.dialerId, target?.location]);

  if (!target) return null;

  const allocatedIds = new Set(target.allocated.map((c) => c.cid));
  const available = runningCampaigns
    .filter((r) => !allocatedIds.has(r.campaignId))
    .filter(
      (r) =>
        !filter ||
        r.campaignName.toLowerCase().includes(filter.toLowerCase()) ||
        String(r.campaignId).includes(filter),
    );

  const handleConfirm = () => {
    if (selectedCid == null) {
      toast.error('Pick a campaign to pin.');
      return;
    }
    const n = Number(percentage);
    if (!Number.isFinite(n) || n < 1 || n > 100) {
      toast.error('Percentage must be between 1 and 100.');
      return;
    }
    const camp = runningCampaigns.find((r) => r.campaignId === selectedCid);
    if (!camp) return;
    onSubmit({
      cid: camp.campaignId,
      cmpName: camp.campaignName,
      percentage: n,
    });
  };

  return (
    <Dialog open={target !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            Pin campaign · dialer #{target.dialerId}
          </DialogTitle>
          <DialogDescription>
            Cap a campaign on this special dialer at a success-rate percentage.
            Existing pins on this dialer are listed at the bottom.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="space-y-2">
            <Label>Campaign</Label>
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search running campaigns…"
            />
            <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-border p-2">
              {loadingRunning ? (
                <div className="py-6 text-center text-xs text-muted-foreground">
                  Loading running campaigns…
                </div>
              ) : available.length === 0 ? (
                <div className="py-6 text-center text-xs text-muted-foreground">
                  No running campaigns are available to pin here.
                </div>
              ) : (
                available.map((c) => {
                  const active = selectedCid === c.campaignId;
                  return (
                    <button
                      key={c.campaignId}
                      type="button"
                      onClick={() =>
                        setSelectedCid(active ? null : c.campaignId)
                      }
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md border p-2 text-left text-sm transition-colors',
                        active
                          ? 'border-primary bg-primary/5'
                          : 'border-transparent hover:bg-muted/50',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                          active
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-muted-foreground/40',
                        )}
                      >
                        {active && <Plus className="h-2.5 w-2.5" />}
                      </span>
                      <span className="truncate">{c.campaignName}</span>
                      <span className="ml-auto font-mono text-xs text-muted-foreground">
                        #{c.campaignId}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="percentage">
              Percentage cap (1–100)
              <span className="ml-0.5 text-destructive">*</span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="percentage"
                inputMode="numeric"
                value={percentage}
                onChange={(e) =>
                  setPercentage(e.target.value.replace(/\D/g, ''))
                }
                maxLength={3}
                placeholder="e.g. 75"
                className="w-132"
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>

          {target.allocated.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-2xs uppercase tracking-wider text-muted-foreground">
                Already pinned
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {target.allocated.map((c) => (
                  <Badge key={c.cid} variant="outline" className="font-normal">
                    <Percent className="h-3 w-3" />
                    {c.cmpName}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting || selectedCid == null || !percentage}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Percent className="h-4 w-4" />
            )}
            Pin at {percentage || '—'}%
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
